import {
  IVariant,
  useFlag as _useFlag,
  useVariant as _useVariant,
  useFlagsStatus,
} from "@unleash/nextjs/client";
import { useEffect, useState } from "react";

import { useCookie, useEffectOnce } from "@chef/hooks";
import { useTrack } from "@chef/state-management/hooks";
import { FEATURE_FLAGS } from "@chef/constants";

/**
 * Checks if the feature flag is enabled or not.
 * This function will also log the event "experimentStarted" when applicable.
 * @param {typeof FEATURE_FLAGS[keyof typeof FEATURE_FLAGS]} name
 * @returns {boolean} enabled
 */
export const useFlag = (
  name: typeof FEATURE_FLAGS[keyof typeof FEATURE_FLAGS],
) => {
  const unleashValue = _useFlag(name);
  const { flagsReady } = useFlagsStatus();

  const [value, setValue] = useState(unleashValue);

  const { value: overrides } = useCookie("vercel-flag-overrides");

  useEffect(() => {
    if (!flagsReady) {
      return;
    }
    if (!overrides) {
      setValue(unleashValue);
      return;
    }

    try {
      const parsed = JSON.parse(overrides) as Record<string, boolean>;
      const override = parsed[name];

      if (override !== undefined) {
        setValue(override);
      } else {
        setValue(unleashValue);
      }
    } catch (e) {
      setValue(unleashValue);
    }
  }, [unleashValue, overrides, name, flagsReady]);

  useTrackExperiment(name, value);

  return { enabled: flagsReady ? value : null, isReady: flagsReady };
};

/**
 * Checks if the the feature flag is enabled, and returns the enabled variant.
 * This function will also log the event "experimentStarted" when applicable.
 * @param {string} name
 * @returns {IVariant} variant
 */
export const useVariant = (name: string) => {
  const unleashValue = _useVariant(name);
  const { flagsReady } = useFlagsStatus();

  const [value, setValue] = useState(unleashValue);

  const { value: overrides } = useCookie("vercel-flag-overrides");

  useEffect(() => {
    if (!overrides) {
      setValue(unleashValue);
      return;
    }

    try {
      const parsed = JSON.parse(overrides) as Record<string, string>;
      const override = parsed[name];

      if (override) {
        setValue({ enabled: true, name: override });
      } else {
        setValue(unleashValue);
      }
    } catch (e) {
      setValue(unleashValue);
    }
  }, [unleashValue, overrides, name]);

   useTrackExperiment(name, value);

  return { variant: flagsReady ? value : null, isReady: flagsReady };
};

export const useTrackExperiment = (
  name?: string,
  value?: boolean | Partial<IVariant>,
) => {
  const track = useTrack();
  const { flagsReady } = useFlagsStatus();

  useEffectOnce(() => {
    if (!name || !value) {
      return;
    }

    if (typeof value === "boolean") {
      if (typeof window !== "undefined") {
        window.DD_RUM?.addFeatureFlagEvaluation(name, value);
      }

      track("experimentStarted", {
        affiliation: "Frontend process",
        flag_name: name,
        enabled: value,
        flag_type: "default",
      });
    } else {
      const enabled = value.enabled ?? false;
      const variantName = value.name!;

      if (typeof window !== "undefined") {
        window.DD_RUM?.addFeatureFlagEvaluation(name, variantName);
      }

      track("experimentStarted", {
        affiliation: "Frontend process",
        flag_name: name,
        enabled,
        flag_type: "variants",
        variant: {
          name: variantName,
          value: enabled.toString(),
        },
        variant_id: variantName,
        variant_label:
          value.payload?.type === "string" ? value.payload?.value : variantName,
      });
    }
  }, flagsReady && !!name && !!value);
};
